import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { colors, spacing, typography } from '@/lib/design-tokens';

type SpinnerSize = 'small' | 'medium' | 'large';

interface LoadingSpinnerProps {
  size?: SpinnerSize;
  label?: string;
}

interface LoadingOverlayProps {
  label?: string;
}

const SPINNER_SIZES: Record<SpinnerSize, number> = {
  small: 16,
  medium: 24,
  large: 40,
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinnerContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[200]};
`;

const SpinnerIcon = styled.div<{ spinnerSize: number }>`
  width: ${({ spinnerSize }) => spinnerSize}px;
  height: ${({ spinnerSize }) => spinnerSize}px;
  border: 2px solid ${colors.neutral[200]};
  border-top-color: ${colors.brand.blue[600]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SpinnerLabel = styled.span`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.small};
  color: ${colors.neutral[600]};
  line-height: ${typography.lineHeight.normal};
`;

function LoadingSpinner({ size = 'medium', label }: LoadingSpinnerProps) {
  return (
    <SpinnerContainer role="status" aria-label="読み込み中">
      <SpinnerIcon spinnerSize={SPINNER_SIZES[size]} />
      {label && <SpinnerLabel>{label}</SpinnerLabel>}
    </SpinnerContainer>
  );
}

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[300]};
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 900;
`;

const OverlayLabel = styled.span`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  color: ${colors.neutral[700]};
  font-weight: ${typography.fontWeight.medium};
  line-height: ${typography.lineHeight.normal};
`;

function LoadingOverlay({ label }: LoadingOverlayProps) {
  return (
    <OverlayContainer data-testid="loading-overlay" aria-busy="true">
      <LoadingSpinner size="large" />
      {label && <OverlayLabel>{label}</OverlayLabel>}
    </OverlayContainer>
  );
}

export type { LoadingOverlayProps, LoadingSpinnerProps, SpinnerSize };
export { LoadingOverlay, LoadingSpinner };
